<script>
  import { fade, fly } from "svelte/transition"
  let twitterUsername = ""
  let dmMessage = ""
  let generatedUrl
  let isGeneratingLink = false
  let isNotificationVisible = false

  const handleShowNotification = () => (isNotificationVisible = true)

  const handleHideNotification = () => (isNotificationVisible = false)

  async function getTwitterIdFromUsername(username) {
    const res = await fetch("/api/twitterId", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username }),
    })
    const id = await res.text()
    return id
  }

  async function handleSubmit() {
    // Set loading to true
    isGeneratingLink = true
    // Make sure they gave us a username
    if (twitterUsername === "") {
      return
    }
    // Make sure they didn't include the @ symbol
    // and trim the whitespace
    const cleanedTwitter = twitterUsername.replace("@", "").trim()

    try {
      const twitterId = await getTwitterIdFromUsername(cleanedTwitter)

      const encodedText = encodeURIComponent(dmMessage)

      // this ensures that we don't include encodedText if dmMessage is empty
      const dmUrlWithText = `https://twitter.com/messages/compose?recipient_id=${twitterId}${
        dmMessage !== "" ? `&text=${encodedText}` : ""
      }
      `

      generatedUrl = dmUrlWithText
    } catch (error) {
      console.log(
        `Couldn't get Twitter ID using the username provided: ${cleanedTwitter}`,
        error
      )
    }

    try {
      await navigator.clipboard.writeText(generatedUrl)
      // Set loading to false to stop showing spinner
      isGeneratingLink = false

      // Show the notification which says "link copied to clipboard"
      handleShowNotification()

      // auto-dismiss the notification after 2.5 secs
      setTimeout(() => handleHideNotification(), 2500)
    } catch (error) {
      // Set loading to false
      isGeneratingLink = false
      console.log(`Couldn't save link to clipbaord`, error)
    }
  }
</script>

<div class="text-xl">
  <nav>
    <a href="/">
      <h1 class="left-0 top-0 ml-4 my-4 font-thin">
        Better Twitter DM Link
      </h1></a>
  </nav>
  <main class="max-w-xl mx-auto">
    <div class="m-4">
      <div class="text-left">
        <p class="font-bold text-4xl mb-8">
          Create a Twitter DM link with a custom message
        </p>
      </div>

      <form on:submit|preventDefault={handleSubmit}>
        <div class="mb-4">
          <label
            for="twitter-username"
            class="block text-black-500 font-bold mb-1 md:mb-0">
            Twitter Username: <input placeholder="jsjoeio" bind:value={twitterUsername} id="twitter-username" class="bg-white-200
                appearance-none border-4 border-gray-200 rounded w-full py-2
                px-4 text-black-700 leading-tight focus:outline-none
                focus:bg-white focus:border-purple-500" type="text" />
          </label>
        </div>
        <label for="dm-message" class="block">
          <span class="text-black-700 font-bold">DM Message:</span>
          <textarea
            id="dm-message"
            bind:value={dmMessage}
            placeholder="Hi! Thanks for doing that awesome thing."
            class="form-textarea mt-1 block w-full bg-white-200 appearance-none
              border-4 border-gray-200 rounded w-full py-2 px-4 text-black-700
              leading-tight focus:outline-none focus:bg-white
              focus:border-purple-500"
            rows="3" />
        </label>
        <div class="flex justify-between mt-8">
          <button
            style="min-height: 48px;"
            class={`bg-purple-600 hover:bg-purple-900 text-gray-200 font-bold py-2 px-4 rounded inline-flex items-center transition ease-in-out duration-150 ${isGeneratingLink || twitterUsername === '' ? 'cursor-not-allowed' : ''}`}
            disabled={isGeneratingLink || twitterUsername === ''}>
            {#if isGeneratingLink}
              <svg
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4" />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg> Generating
            {:else}Generate link{/if}
          </button>

          {#if isNotificationVisible}
            <div
              in:fade
              out:fly={{ x: 100 }}
              class="bg-teal-100 rounded text-teal-900 px-2 py-3 shadow-md"
              role="alert">
              <div class="flex">
                <div>
                  <svg
                    class="fill-current h-6 w-6 text-teal-500 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clip-rule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p class="text-sm">Link copied to clipboard</p>
                </div>
              </div>
            </div>
          {/if}
        </div>
      </form>
      <div class="mt-4">
        {#if generatedUrl}
          <a
            in:fly={{ delay: 200 }}
            class="underline text-blue-900 break-words"
            href={generatedUrl}
            target="_blank">{generatedUrl}</a>
        {/if}
      </div>
    </div>
  </main>

  <footer>
    <div
      class="flex space-x-4 items-center justify-center fixed bottom-0 inset-x-0
        pb-4 font-thin text-lg">
      <div>
        <a
          class="hover:underline flex items-end"
          href="https://twitter.com/intent/tweet?text=https%3A%2F%2Fbetter-twitter-dm-link.vercel.app%2F%20by%20%40jsjoeio%20is%20awesome!%20%F0%9F%9A%80%0A%0ASuper%20handy%20for%20creating%20Twitter%20DM%20links%20with%20custom%20messages!"
          target="_blank">
          <span>Share</span>
          <svg
            class="fill-current h-6 w-6 text-black-500 ml-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
        </a>
      </div>
      <div>
        <a
          class="hover:underline flex items-end"
          href="https://www.buymeacoffee.com/jsjoeio"
          target="_blank">
          <span>Thank</span>
          <svg
            class="fill-current h-6 w-6 ml-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path
              fillRule="evenodd"
              d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z"
              clipRule="evenodd" />
            <path
              d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z" />
          </svg>
        </a>
      </div>
      <div>
        <a
          class="hover:underline flex items-end"
          href="https://twitter.com/intent/user?screen_name=jsjoeio"
          target="_blank">
          <span>Follow</span>
          <svg
            class="fill-current h-6 w-6 text-black-500 ml-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path
              d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
          </svg>
        </a>
      </div>
    </div>
  </footer>
</div>
